import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/Button';
import { Input, Label } from '@/components/Input';
import { Card } from '@/components/Card';
import { friendlyAuthError } from '@/lib/authErrors';
export { friendlyAuthError as friendly } from '@/lib/authErrors';

export function SignIn() {
  const { signInEmail, signInGoogle, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (user && !user.isAnonymous) { nav('/'); return; }
    setErr(null);
    setBusy(true);
    try {
      await signInEmail(email, password);
      nav('/');
    } catch (e) {
      console.warn('[auth] email sign-in failed', { code: (e as { code?: string }).code ?? 'unknown' });
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    if (busy) return;
    if (user && !user.isAnonymous) { nav('/'); return; }
    setErr(null);
    setBusy(true);
    try {
      await signInGoogle();
      nav('/');
    } catch (e) {
      console.warn('[auth] Google sign-in failed', { code: (e as { code?: string }).code ?? 'unknown' });
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Welcome back">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {err && <p role="alert" aria-live="assertive" className="text-sm text-rose-700 dark:text-rose-300">{err}</p>}
        <Button type="submit" size="lg" loading={busy} disabled={busy} className="w-full">
          Sign in
        </Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-stone-500">
        <span className="flex-1 h-px bg-stone-200 dark:bg-stone-800" />
        <span>or</span>
        <span className="flex-1 h-px bg-stone-200 dark:bg-stone-800" />
      </div>
      <Button variant="secondary" size="lg" onClick={onGoogle} loading={busy} disabled={busy} className="w-full">
        Continue with Google
      </Button>
      <p className="mt-6 text-center text-sm text-stone-600 dark:text-stone-400">
        New here?{' '}
        <Link to="/signup" className="text-brand-700 dark:text-brand-400 font-medium">
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-bold mb-1 text-brand-700 dark:text-brand-400">CNA Prep Pro</h1>
        <p className="text-stone-600 dark:text-stone-400 mb-6">{title}</p>
        <Card>{children}</Card>
      </div>
    </div>
  );
}
